import { useMutation } from "@apollo/client";
import { DownloadBox } from "atoms";
import {
    constantsData,
    identifiersData,
    namesData,
    statusTypesData,
} from "data";
import { exportVideosMutation } from "graph";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { openAlert } from "redux/actions";
import { displayErrorHandler, getUniqueRandomId } from "utils";

const useExportVideos = () => {
    const [exporting, setExporting] = useState(false);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [exportVideosAction, { loading }] = useMutation(exportVideosMutation);

    const {
        exportVideosPending: exportVideosPendingToastName,
        exportVideosSuccess: exportVideosSuccessToastName,
    } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const exportVideos = async (ids) => {
        setExporting(true);

        try {
            await exportVideosAction({
                onCompleted: (data) => {
                    setExporting(false);

                    const randomId = getUniqueRandomId();

                    toast.success(
                        () => (
                            <DownloadBox
                                id={`${exportVideosSuccessToastName}_${toastId}_${randomId}`}
                                link={data.exportVideos}
                                message={t(
                                    "messages.downloadMsgs.download",
                                    { entity: t("videos.name") },
                                )}
                            />
                        ),
                        {
                            closeButton: true,
                            toastId: `${exportVideosSuccessToastName}_${toastId}_${randomId}`,
                        },
                    );

                    dispatch(openAlert(
                        t(
                            "messages.exportMsgs.success",
                            { entity: t("videos.name") },
                        ),
                        statusTypesData.success,
                    ));
                },
                variables: {
                    toCsv: true,
                    videosIds: ids,
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${exportVideosPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading && exporting) {
                toast.info(
                    () => t(
                        "messages.exportMsgs.pending",
                        { entity: t("videos.name") },
                    ),
                    { toastId: `${exportVideosPendingToastName}_${toastId}` },
                );
            }
        },
        [loading, exporting], //eslint-disable-line
    );

    return { exportVideos };
};

export default useExportVideos;
